import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { lightGreen, blue } from '@mui/material/colors';
import { Charge, ChargeAttachType } from 'modules/equipment/types';
import { Box, IconButton, Menu, MenuItem, Typography, Tooltip, Stack } from '@mui/material';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { Link, LinkOff, RemoveCircle, SubdirectoryArrowRight } from '@mui/icons-material';
import { buildBadgeLabel } from '../../../../../lib';

interface Props {
  charge: Charge;
  onRemoveRow: VoidFunction;
  onUnLinkCharge: VoidFunction;
  onLinkCharge: (attachId: string, linkIndex: number) => void;
  itemsForAttach: Array<{ id: string; description: string; rowIndex: number }>;
}

export const InvoiceDescriptionCell = ({
  charge,
  onRemoveRow,
  onLinkCharge,
  onUnLinkCharge,
  itemsForAttach,
}: Props) => {
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  if (charge.type === undefined) throw new Error('Charge type is required');

  const renderDataByType: Record<ChargeAttachType, { bg: string; label: string; tooltip: string }> = {
    [ChargeAttachType.Invoice]: {
      bg: lightGreen[100],
      label: 'Invoice Level',
      tooltip: 'Link to Equipment',
    },
    [ChargeAttachType.Attachment]: {
      bg: blue[100],
      label: buildBadgeLabel(charge.attachTo.type),
      tooltip: 'Unlink charge',
    },
    [ChargeAttachType.Equipment]: {
      bg: blue[200],
      label: buildBadgeLabel(charge.attachTo.type),
      tooltip: 'Unlink charge',
    },
  };

  const handleSelectAttachItem = (value: string, index: number) => {
    setAnchorEl(null);
    onLinkCharge(value, index);
  };

  const handleUpdateLink = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (charge.attachTo.type === ChargeAttachType.Invoice) {
      setAnchorEl(event.currentTarget);
    } else {
      onUnLinkCharge();
    }
  };

  return (
    <>
      <Box className={classes.root}>
        <RenderWithCondition condition={charge.attachTo.type !== ChargeAttachType.Invoice}>
          <SubdirectoryArrowRight color="disabled" />
        </RenderWithCondition>

        <Box className="content">
          <Stack direction="row" gap="0.3rem">
            <Typography
              variant="caption"
              className="badge"
              sx={{ backgroundColor: renderDataByType[charge.attachTo.type].bg }}
            >
              {charge.attachTo?.description || renderDataByType[charge.attachTo.type].label}
            </Typography>

            {charge.isFromCommonRules && (
              <Typography variant="caption" className="badge" sx={{ backgroundColor: blue[100] }}>
                Global
              </Typography>
            )}
          </Stack>

          <Box className="row">
            <Box className="left-side">
              <Tooltip title="Remove row">
                <IconButton className="icon-btn-bordered" color="error" size="small" onClick={onRemoveRow}>
                  <RemoveCircle fontSize="small" />
                </IconButton>
              </Tooltip>

              <Typography>{charge.label}</Typography>
            </Box>

            <Tooltip title={renderDataByType[charge.attachTo.type].tooltip}>
              <IconButton size="small" className="icon-btn-bordered" onClick={handleUpdateLink}>
                {charge.attachTo.type === ChargeAttachType.Invoice ? (
                  <Link sx={{ fontSize: '21px' }} color="primary" />
                ) : (
                  <LinkOff sx={{ fontSize: '21px' }} color="primary" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
        {itemsForAttach.map(({ id, description, rowIndex }) => (
          <MenuItem
            key={id}
            onClick={() => {
              handleSelectAttachItem(id, rowIndex + 1);
            }}
          >
            {description}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    width: '100%',

    '& .content': {
      gap: '0.5rem',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      marginTop: '0.3rem',

      '& .badge': {
        borderRadius: '4px',
        width: 'fit-content',
        padding: '1px 0.25rem',
      },

      '& .row': {
        gap: '0.5rem',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',

        '& .left-side': {
          gap: '0.5rem',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
        },
      },
    },
  },
});
